import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Tag, Modal, Form,
  message, Popconfirm, Tooltip
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

interface Project {
  projectId: number;
  projectNo: string;
  casNo: string;
  name: string;
  nameEn: string;
  description: string;
  productType: string;
  solutionType: 'auto' | 'semi';
  phase: 'npi' | 'mass' | 'eol';
  location: string;
  customer: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  createdAt: string;
  updatedAt: string;
}

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [searchParams, setSearchParams] = useState<any>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [form] = Form.useForm();

  const fetchProjects = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        ...searchParams
      });
      
      const res = await fetch(`/api/projects?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await res.json();
      if (data.success) {
        setProjects(data.data.list);
        setPagination({ ...pagination, page, pageSize, total: data.data.pagination.total });
      }
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [searchParams]);

  const handleSearch = (keyword: string) => {
    setSearchParams((prev: any) => ({ ...prev, keyword }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams((prev: any) => ({ ...prev, [key]: value || undefined }));
  };

  const handleCreate = () => {
    setEditingProject(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Project) => {
    setEditingProject(record);
    form.setFieldsValue({
      projectNo: record.projectNo,
      casNo: record.casNo,
      name: record.name,
      nameEn: record.nameEn,
      description: record.description,
      productType: record.productType,
      solutionType: record.solutionType,
      phase: record.phase,
      location: record.location,
      customer: record.customer
    });
    setModalVisible(true);
  };

  const handleView = async (record: Project) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/projects/${record.projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (data.success) {
        setSelectedProject(data.data);
        setDetailVisible(true);
      }
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const url = editingProject 
        ? `/api/projects/${editingProject.projectId}`
        : '/api/projects';
      
      const res = await fetch(url, {
        method: editingProject ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          projectNo: values.projectNo,
          casNo: values.casNo,
          name: values.name,
          nameEn: values.nameEn,
          description: values.description,
          productType: values.productType,
          solutionType: values.solutionType,
          phase: values.phase,
          location: values.location,
          customer: values.customer
        })
      });

      const data = await res.json();
      if (data.success) {
        message.success(t('common.success'));
        setModalVisible(false);
        fetchProjects(pagination.page, pagination.pageSize);
      } else {
        message.error(data.message || t('common.error'));
      }
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`/api/projects/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await res.json();
      if (data.success) {
        message.success(t('common.success'));
        fetchProjects(pagination.page, pagination.pageSize);
      }
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      active: 'processing',
      completed: 'success',
      archived: 'warning'
    };
    return colors[status] || 'default';
  };

  const getPhaseColor = (phase: string) => {
    const colors: Record<string, string> = {
      npi: 'blue',
      mass: 'green',
      eol: 'orange'
    };
    return colors[phase] || 'default';
  };

  const columns = [
    {
      title: t('project.projectNo'),
      dataIndex: 'projectNo',
      key: 'projectNo',
      width: 120,
      render: (text: string) => <a onClick={() => {}}>{text}</a>
    },
    {
      title: t('project.casNo'),
      dataIndex: 'casNo',
      key: 'casNo',
      width: 100
    },
    {
      title: t('project.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true
    },
    {
      title: t('project.productType'),
      dataIndex: 'productType',
      key: 'productType',
      width: 100
    },
    {
      title: t('project.solutionType'),
      dataIndex: 'solutionType',
      key: 'solutionType',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'auto' ? 'green' : 'blue'}>
          {t(`project.${type}`)}
        </Tag>
      )
    },
    {
      title: t('project.phase'),
      dataIndex: 'phase',
      key: 'phase',
      width: 100,
      render: (phase: string) => (
        <Tag color={getPhaseColor(phase)}>{t(`project.${phase}`)}</Tag>
      )
    },
    {
      title: t('project.location'),
      dataIndex: 'location',
      key: 'location',
      width: 100
    },
    {
      title: t('project.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{t(`project.${status}`)}</Tag>
      )
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Project) => (
        <Space size="small">
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          <Popconfirm title={t('common.confirm')} onConfirm={() => handleDelete(record.projectId)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder={t('common.search')}
            allowClear
            onSearch={handleSearch}
            style={{ width: 250 }}
            prefix={<SearchOutlined />}
          />
          <Select
            style={{ width: 120 }}
            placeholder={t('project.status')}
            allowClear
            onChange={(v) => handleFilterChange('status', v)}
          >
            <Option value="draft">{t('project.draft')}</Option>
            <Option value="active">{t('project.active')}</Option>
            <Option value="completed">{t('project.completed')}</Option>
            <Option value="archived">{t('project.archived')}</Option>
          </Select>
          <Select
            style={{ width: 120 }}
            placeholder={t('project.phase')}
            allowClear
            onChange={(v) => handleFilterChange('phase', v)}
          >
            <Option value="npi">{t('project.npi')}</Option>
            <Option value="mass">{t('project.mass')}</Option>
            <Option value="eol">{t('project.eol')}</Option>
          </Select>
          <Select
            style={{ width: 120 }}
            placeholder={t('project.solutionType')}
            allowClear
            onChange={(v) => handleFilterChange('solutionType', v)}
          >
            <Option value="auto">{t('project.auto')}</Option>
            <Option value="semi">{t('project.semi')}</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => fetchProjects()}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('common.create')}
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={projects}
          rowKey="projectId"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) => fetchProjects(page, pageSize)
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingProject ? t('project.editProject') : t('project.addProject')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="projectNo"
            label={t('project.projectNo')}
            rules={[{ required: true, message: `${t('common.required')}` }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="casNo" label={t('project.casNo')}>
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('project.name')}
            rules={[{ required: true, message: `${t('common.required')}` }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="nameEn" label={t('project.nameEn')}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('project.description')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="productType" label={t('project.productType')}>
            <Input />
          </Form.Item>
          <Form.Item name="solutionType" label={t('project.solutionType')} initialValue="semi">
            <Select>
              <Option value="auto">{t('project.auto')}</Option>
              <Option value="semi">{t('project.semi')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="phase" label={t('project.phase')} initialValue="npi">
            <Select>
              <Option value="npi">{t('project.npi')}</Option>
              <Option value="mass">{t('project.mass')}</Option>
              <Option value="eol">{t('project.eol')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="location" label={t('project.location')}>
            <Select allowClear>
              <Option value="Thailand">Thailand</Option>
              <Option value="Mexico">Mexico</Option>
              <Option value="China Haiyan">China Haiyan</Option>
              <Option value="China Haining">China Haining</Option>
            </Select>
          </Form.Item>
          <Form.Item name="customer" label={t('project.customer')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`${t('project.projectNo')}: ${selectedProject?.projectNo}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={800}
      >
        {selectedProject && (
          <div>
            <p><strong>{t('project.name')}:</strong> {selectedProject.name}</p>
            <p><strong>{t('project.casNo')}:</strong> {selectedProject.cas_no || '-'}</p>
            <p><strong>{t('project.productType')}:</strong> {selectedProject.product_type || '-'}</p>
            <p><strong>{t('project.solutionType')}:</strong> {selectedProject.solution_type}</p>
            <p><strong>{t('project.phase')}:</strong> {selectedProject.phase}</p>
            <p><strong>{t('project.location')}:</strong> {selectedProject.location || '-'}</p>
            <p><strong>{t('project.customer')}:</strong> {selectedProject.customer || '-'}</p>
            <p><strong>{t('project.description')}:</strong> {selectedProject.description || '-'}</p>
            
            {selectedProject.routes?.length > 0 && (
              <>
                <h4 style={{ marginTop: 16 }}>{t('ie.route')}</h4>
                <Table
                  size="small"
                  dataSource={selectedProject.routes}
                  rowKey="route_id"
                  columns={[
                    { title: 'Route Name', dataIndex: 'route_name', key: 'route_name' },
                    { title: 'Version', dataIndex: 'version', key: 'version' }
                  ]}
                  pagination={false}
                />
              </>
            )}
            
            {selectedProject.costs?.length > 0 && (
              <>
                <h4 style={{ marginTop: 16 }}>{t('cost.result')}</h4>
                <Table
                  size="small"
                  dataSource={selectedProject.costs}
                  rowKey="cost_id"
                  columns={[
                    { title: 'Version', dataIndex: 'version', key: 'version' },
                    { title: t('cost.totalCost'), dataIndex: 'total_cost', key: 'total_cost', render: (v: number) => `$${v?.toFixed(2)}` },
                    { title: t('common.status'), dataIndex: 'status', key: 'status' }
                  ]}
                  pagination={false}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Projects;
